import { create } from 'zustand'
import { devtools } from 'utils/devtools'
import { immer } from 'zustand/middleware/immer'
import nanoid from '@ui/utils/nanoid'
import { PaymentMethod, PaymentProvider, PaymentResponse } from '@ca/common'
import { PaymentConfig } from '@order/orderTypes'
import { getMethodSettings } from './usePaymentMethods'
import { hasPaymentMetadataOrders } from '@ca/common'
import type { PaymentMetadata } from '@ca/common'
import type { SavedOrder } from './state/orderState'
import type { GlobemedAttempt } from 'features/payment/globemed/globemedAttempt'

export type PaymentDetails = PaymentConfig & {
  localId: string
  orderIds: string[]
  link?: string
  metadata?: PaymentMetadata
  sourceOrders?: SavedOrder[]
}

type PaymentActions = {
  createPayment: (payment: Omit<PaymentDetails, 'status' | 'localId'>) => string
  updatePaymentMethod: (
    localId: string,
    payment: Pick<PaymentConfig, 'method' | 'amount' | 'reader_id' | 'provider'>
  ) => void
  handlePaymentResponse: (
    localId: string,
    status: PaymentResponse<PaymentMethod, PaymentProvider>
  ) => void
  handlePaymentSuccess: (localId: string) => void
  handleCancelPayment: (localId: string) => void
  removeDraftPayment: (localId: string) => void
  setMetadata: (localId: string, metadata: PaymentMetadata) => void
  setGlobemedAttempt: (orderIds: number[], attempt: GlobemedAttempt) => void
  clearGlobemedAttempt: (orderIds: number[]) => void
  markOrdersSubmitted: (orderIds: number[]) => void
  reset: () => void
}

// Cleared on logout. Order-scoped entries only live for the app process; if they
// need to survive restarts, refetch order status/payments from order history.
type PaymentsState = {
  payments: Record<string, PaymentDetails>
  submittedOrderIds: Record<number, true>
  // Keyed by ERP order set so it outlives draft payments
  globemedAttempts: Record<string, GlobemedAttempt>
}

export const getOrderSetKey = (orderIds: readonly number[]) =>
  [...orderIds].sort((a, b) => a - b).join(',')

const usePayments = create<PaymentsState & PaymentActions>()(
  devtools(
    immer((set) => ({
      payments: {},
      submittedOrderIds: {},
      globemedAttempts: {},

      reset: () => {
        set((state) => {
          state.payments = {}
          state.submittedOrderIds = {}
          state.globemedAttempts = {}
        })
      },
      createPayment: (payment) => {
        const id = nanoid()
        set((state) => {
          const item = {
            localId: id,
            ...payment,
            status: 'draft',
          } satisfies PaymentDetails

          state.payments[id] = item
        })
        return id
      },
      updatePaymentMethod: (localId, payment) => {
        set((state) => {
          const item = state.payments[localId]
          if (!item) return

          if (item.status === 'completed' || item.status === 'waiting') {
            return
          }

          item.reader_id = payment.reader_id
          item.method = payment.method
          item.amount = payment.amount
          item.provider = payment.provider
        })
      },
      handlePaymentResponse: (localId, res) => {
        set((state) => {
          const payment = state.payments[localId]
          if (!payment) return

          // Consider a nicer way of resolving the type?
          if ('payment_id' in res) {
            payment.paymentId = res.payment_id
          }
          if ('link' in res) {
            payment.link = res.link
          }

          const settings = getMethodSettings(res.method, res.provider)
          payment.status = settings.createStatus
        })
      },
      handlePaymentSuccess: (localId) => {
        set((state) => {
          const payment = state.payments[localId]
          if (!payment) return
          payment.status = 'completed'
        })
      },
      handleCancelPayment: (localId) => {
        set((state) => {
          const payment = state.payments[localId]

          if (!payment) return
          payment.status = 'draft'
        })
      },
      removeDraftPayment: (localId) => {
        set((state) => {
          const payment = state.payments[localId]
          if (!payment || payment.status !== 'draft') return
          delete state.payments[localId]
        })
      },
      setMetadata: (localId, metadata) => {
        if (!hasPaymentMetadataOrders(metadata)) return
        set((state) => {
          const payment = state.payments[localId]
          if (!payment) return
          payment.metadata = metadata
        })
      },
      setGlobemedAttempt: (orderIds, attempt) => {
        set((state) => {
          state.globemedAttempts[getOrderSetKey(orderIds)] = attempt
        })
      },
      clearGlobemedAttempt: (orderIds) => {
        set((state) => {
          delete state.globemedAttempts[getOrderSetKey(orderIds)]
        })
      },
      markOrdersSubmitted: (orderIds) => {
        set((state) => {
          orderIds.forEach((id) => {
            state.submittedOrderIds[id] = true
          })
        })
      },
    })),
    {
      name: 'payments',
    }
  )
)

export default usePayments
